import React from 'react';
import { Center, Stack } from '@fiverr-private/layout_components';
import { Button } from '@fiverr-private/button';
import { NumberedList, ListItem } from '@fiverr-private/list';
import { Video } from '@fiverr-private/media';
import { ExternalLinkIcon } from '@fiverr-private/visuals';
import { ElementType } from '@fiverr-private/bianka_js';
import { getContext } from '@fiverr-private/fiverr_context';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { GIF_URL } from '../sign-up/SignUp.constants';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { StepTitle } from '../../components/step-title/StepTitle';
import { GifWrapper } from '../../components/gif-wrapper/GifWrapper';
import { replaceShopifyDomain } from '../../utils/replaceShopifyDomain';
import { useStoreData } from '../../hooks/useStoreData';
import { SHOPIFY_URL } from './Publish.constants';

const LIST_ITEMS = [
    {
        id: 1,
        text: <AppI18n k="steps.publish.list.item_1" />,
    },
    {
        id: 2,
        text: <AppI18n k="steps.publish.list.item_2" templates={{ strong: (text) => <strong>{text}</strong> }} />,
    },
    {
        id: 3,
        text: <AppI18n k="steps.publish.list.item_3" />,
    },
];

export const Publish: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const { storeData } = useStoreData();
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const handlePublishMyStoreClick = () => {
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_publish_my_store',
            elementType: ElementType.LINK,
            currentPath,
        });

        if (!storeData?.storeUrl) return;
        const shopifyUrl = replaceShopifyDomain(SHOPIFY_URL, storeData.storeUrl);
        window.open(shopifyUrl, '_blank');
    };

    return (
        <Center>
            <Stack direction="column" gap="2" color="grey_1200" ref={pageImpressionRef}>
                <StepTitle title={<AppI18n k="steps.publish.title" />} />
                <NumberedList rowGap="4" paddingLeft="4">
                    {LIST_ITEMS.map(({ id, text }) => (
                        <ListItem key={id} marginBottom="2">
                            {text}
                        </ListItem>
                    ))}
                </NumberedList>
                <GifWrapper marginY="6">
                    <Video
                        sources={[
                            {
                                type: 'video/mp4',
                                src: GIF_URL,
                            },
                        ]}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls={false}
                        width={{ default: 310, sm: 488 }}
                    />
                </GifWrapper>
                <Button onClick={handlePublishMyStoreClick} alignSelf="flexStart" variant="outline">
                    <AppI18n k="steps.publish.cta" />
                    <ExternalLinkIcon />
                </Button>
            </Stack>
        </Center>
    );
};

Publish.displayName = 'Publish';
